interface DiagnosticEntry {
  at: string
  kind: 'api' | 'error' | 'rejection'
  message: string
  method?: string
  path?: string
  status?: number
  source?: string
  line?: number
  column?: number
  /** Id запроса гейтвея (X-Request-Id / error.requestId) — ссылка на pim_server_errors. */
  requestId?: string
}
const MAX_ENTRIES = 20
const REQUEST_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const diagnostics: DiagnosticEntry[] = []
let initialized = false

function redact(value: unknown): string {
  return String(value || '')
    .replace(/eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g, '[token]')
    .replace(/(password|token|authorization|cookie)=?[^&\s]*/gi, '$1=[redacted]')
    .slice(0, 500)
}

function safePath(value: unknown): string | undefined {
  if (!value) return undefined
  try {
    const parsed = new URL(String(value), window.location.origin)
    return `${parsed.origin === window.location.origin ? '' : parsed.origin}${parsed.pathname}`
  } catch {
    return String(value).split(/[?#]/)[0].slice(0, 500)
  }
}

function push(entry: DiagnosticEntry) {
  diagnostics.push(entry)
  if (diagnostics.length > MAX_ENTRIES) diagnostics.splice(0, diagnostics.length - MAX_ENTRIES)
}

function safeRequestId(value: unknown): string | undefined {
  const raw = Array.isArray(value) ? value[0] : value
  if (typeof raw !== 'string') return undefined
  const trimmed = raw.trim()
  return REQUEST_ID_PATTERN.test(trimmed) ? trimmed.toLowerCase() : undefined
}

/**
 * Id запроса из ответа гейтвея: сначала заголовок X-Request-Id, затем
 * `error.requestId` (или `requestId`) в теле — заголовок может срезать прокси.
 */
export function extractRequestId(headers: unknown, data: unknown): string | undefined {
  const headerBag = headers as { get?: (name: string) => unknown; [key: string]: unknown } | undefined
  const fromHeader = headerBag
    ? (typeof headerBag.get === 'function' ? headerBag.get('x-request-id') : undefined)
      ?? headerBag['x-request-id']
      ?? headerBag['X-Request-Id']
    : undefined
  const body = data && typeof data === 'object' ? (data as Record<string, any>) : undefined
  const fromBody = body?.error && typeof body.error === 'object' ? body.error.requestId : body?.requestId
  return safeRequestId(fromHeader) ?? safeRequestId(fromBody)
}

export function recordApiFailure(input: {
  method?: string
  url?: string
  status?: number
  message?: string
  requestId?: string
}) {
  const requestId = safeRequestId(input.requestId)
  push({
    at: new Date().toISOString(),
    kind: 'api',
    method: input.method?.toUpperCase(),
    path: safePath(input.url),
    status: input.status,
    ...(requestId ? { requestId } : {}),
    message: redact(input.message || 'API request failed'),
  })
}

export function initClientDiagnostics() {
  if (initialized || typeof window === 'undefined') return
  initialized = true
  window.addEventListener('error', (event) => {
    push({
      at: new Date().toISOString(),
      kind: 'error',
      message: redact(event.message),
      source: safePath(event.filename),
      line: event.lineno,
      column: event.colno,
    })
  })
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason instanceof Error ? event.reason.message : event.reason
    push({
      at: new Date().toISOString(),
      kind: 'rejection',
      message: redact(reason || 'Unhandled promise rejection'),
    })
  })
}

export function collectClientContext() {
  const timezone = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch {
      return undefined
    }
  })()
  return {
    browser: {
      userAgent: navigator.userAgent.slice(0, 1000),
      language: navigator.language,
      platform: navigator.platform,
      online: navigator.onLine,
    },
    display: {
      viewport: { width: window.innerWidth, height: window.innerHeight },
      screen: { width: window.screen.width, height: window.screen.height },
      pixelRatio: window.devicePixelRatio,
    },
    application: {
      build:
        import.meta.env.VITE_APP_VERSION
        || import.meta.env.VITE_GIT_COMMIT
        || 'development',
      mode: import.meta.env.MODE,
    },
    timezone,
    diagnostics: diagnostics.slice(-MAX_ENTRIES),
  }
}
